import React, { useEffect, useState } from 'react';
import Cropper from 'react-easy-crop';
import { Avatar, Icon } from '../components/sp-primitives';
import { Modal } from '../components/sp-overlays';
import { PShell, useApp } from '../components/sp-shell';
import 'react-easy-crop/react-easy-crop.css';

export function PProfile() {
  const PERMISSION_LEVELS = ['Registrador', 'Aprovador', 'Administrador'];
  const app = useApp();
  const canManageUsers = Array.isArray(app.currentUser?.permissions) && app.currentUser.permissions.includes('Administrador');
  const readOnlyProfile = !canManageUsers;
  const isCreating = Boolean(app.params?.newUser);
  const selectedUser = isCreating
    ? null
    : (app.users.find((u) => u.id === app.params?.userId) || app.currentUser);
  const isProtectedUser = Number(selectedUser?.id || 0) === 1;
  const permissionsLocked = isProtectedUser;
  const statusLocked = isProtectedUser;
  const fileInputRef = React.useRef(null);
  const [name, setName] = useState(selectedUser?.name || '');
  const [email, setEmail] = useState(selectedUser?.email || '');
  const [department, setDepartment] = useState(selectedUser?.department || '');
  const [phone, setPhone] = useState(selectedUser?.phone || '');
  const [whatsapp, setWhatsapp] = useState(selectedUser?.whatsapp || '');
  const [cityUf, setCityUf] = useState(selectedUser?.city_uf || '');
  const [role, setRole] = useState(selectedUser?.role || '');
  const [status, setStatus] = useState(selectedUser?.status || 'active');
  const [timezone, setTimezone] = useState('América / São Paulo (GMT-3)');
  const [saving, setSaving] = useState(false);
  const [photoFileName, setPhotoFileName] = useState('');
  const [avatarData, setAvatarData] = useState(selectedUser?.avatar_data || '');
  const [rawPhotoData, setRawPhotoData] = useState('');
  const [cropOpen, setCropOpen] = useState(false);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [cropZoom, setCropZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [removePhotoOpen, setRemovePhotoOpen] = useState(false);
  const [confirmDeactivateOpen, setConfirmDeactivateOpen] = useState(false);
  const [deactivatePassword, setDeactivatePassword] = useState('');
  const [deactivating, setDeactivating] = useState(false);
  const [photoRemoved, setPhotoRemoved] = useState(false);
  const [selectedPermissions, setSelectedPermissions] = useState(['Registrador']);
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [submitAttempted, setSubmitAttempted] = useState(false);

  useEffect(() => {
    if (isCreating) {
      setName('');
      setEmail('');
      setDepartment('');
      setPhone('');
      setWhatsapp('');
      setCityUf('');
      setRole('');
      setStatus('active');
      setAvatarData('');
      setPhotoFileName('');
      setRawPhotoData('');
      setCropOpen(false);
      setPhotoRemoved(false);
      setPassword('');
      setPasswordConfirm('');
      setSelectedPermissions(['Registrador']);
      setSubmitAttempted(false);
      return;
    }
    setName(selectedUser?.name || '');
    setEmail(selectedUser?.email || '');
    setDepartment(selectedUser?.department || '');
    setPhone(selectedUser?.phone || '');
    setWhatsapp(selectedUser?.whatsapp || '');
    setCityUf(selectedUser?.city_uf || '');
    setRole(selectedUser?.role || '');
    setStatus(selectedUser?.status || 'active');
    setAvatarData(selectedUser?.avatar_data || '');
    setPhotoFileName('');
    setRawPhotoData('');
    setCropOpen(false);
    setCrop({ x: 0, y: 0 });
    setCroppedAreaPixels(null);
    setPhotoRemoved(false);
    setPassword('');
    setPasswordConfirm('');
    setSubmitAttempted(false);
    const rawPermissions = Array.isArray(selectedUser?.permissions) ? selectedUser.permissions : [];
    const unique = Array.from(new Set(rawPermissions.filter((value) => PERMISSION_LEVELS.includes(value))));
    setSelectedPermissions(unique.length ? unique : ['Registrador']);
  }, [isCreating, selectedUser?.id, selectedUser?.name, selectedUser?.email, selectedUser?.department, selectedUser?.phone, selectedUser?.whatsapp, selectedUser?.city_uf, selectedUser?.role, selectedUser?.status, selectedUser?.avatar_data, selectedUser?.permissions]);

  const perms = selectedPermissions;
  const fieldErrors = {
    name: !String(name || '').trim(),
    role: !String(role || '').trim(),
    email: !String(email || '').trim(),
    department: !String(department || '').trim(),
    cityUf: !String(cityUf || '').trim(),
    status: !String(status || '').trim(),
    password: isCreating && !String(password || '').trim(),
    passwordConfirm: isCreating && !String(passwordConfirm || '').trim(),
  };

  const togglePermission = (perm) => {
    if (permissionsLocked) return;
    setSelectedPermissions((curr) => {
      const has = curr.includes(perm);
      if (has) {
        const next = curr.filter((p) => p !== perm);
        return next.length ? next : curr;
      }
      return [...curr, perm];
    });
  };

  const saveProfile = async () => {
    if (!canManageUsers) {
      app.toast('error', 'Sem permissão', 'Apenas usuários com permissão Administrador podem modificar cadastro de usuários.');
      return;
    }
    setSubmitAttempted(true);
    if (!isCreating && !selectedUser?.id) {
      app.toast('error', 'Usuário inválido', 'Não foi possível identificar o usuário selecionado.');
      return;
    }
    if (fieldErrors.name || fieldErrors.role || fieldErrors.email || fieldErrors.department || fieldErrors.cityUf || fieldErrors.status || fieldErrors.password || fieldErrors.passwordConfirm) {
      app.toast('error', 'Falha ao salvar', 'Preencha os campos obrigatórios destacados em vermelho.');
      return;
    }
    if (isCreating && !String(password || '').trim()) {
      app.toast('error', 'Falha ao salvar', 'Informe uma senha para o novo usuário.');
      return;
    }
    if (password || passwordConfirm) {
      if (password !== passwordConfirm) {
        app.toast('error', 'Falha ao salvar', 'Senha e confirmação de senha não conferem.');
        return;
      }
      if (String(password).length < 6) {
        app.toast('error', 'Falha ao salvar', 'A senha deve ter pelo menos 6 caracteres.');
        return;
      }
    }
    setSaving(true);
    try {
      const payload = {
        name,
        email,
        department,
        phone,
        whatsapp,
        city_uf: cityUf,
        role,
        status,
        avatar_data: avatarData || null,
      };
      if (!isProtectedUser) {
        payload.permissions = selectedPermissions;
      }
      if (password) payload.password = password;
      if (isCreating) {
        await app.createUser(payload);
        app.toast('success', 'Usuário criado', 'Novo usuário cadastrado com sucesso.');
        app.navigate('users');
      } else {
        await app.updateUser(selectedUser.id, {
          ...payload,
        });
        app.toast('success', 'Perfil atualizado', 'Alterações salvas com sucesso.');
      }
      setPassword('');
      setPasswordConfirm('');
    } catch (error) {
      app.toast('error', 'Falha ao salvar', error.message || 'Verifique os dados informados.');
    } finally {
      setSaving(false);
    }
  };

  const onPickPhoto = () => {
    fileInputRef.current?.click();
  };

  const onPhotoSelected = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const isAllowedType = ['image/png', 'image/jpeg'].includes(file.type);
    const isValidSize = file.size <= 2 * 1024 * 1024;

    if (!isAllowedType) {
      app.toast('error', 'Formato inválido', 'Use apenas PNG ou JPG.');
      event.target.value = '';
      return;
    }

    if (!isValidSize) {
      app.toast('error', 'Arquivo muito grande', 'A foto deve ter no máximo 2MB.');
      event.target.value = '';
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result || '');
      if (!result.startsWith('data:image/')) {
        app.toast('error', 'Falha ao abrir imagem', 'Não foi possível carregar a foto selecionada.');
        return;
      }
      setPhotoFileName(file.name);
      setRawPhotoData(result);
      setPhotoRemoved(false);
      setCrop({ x: 0, y: 0 });
      setCropZoom(1);
      setCroppedAreaPixels(null);
      setCropOpen(true);
    };
    reader.onerror = () => {
      app.toast('error', 'Falha ao abrir imagem', 'Não foi possível carregar a foto selecionada.');
    };
    reader.readAsDataURL(file);
  };

  const applyCrop = async () => {
    if (!rawPhotoData || !croppedAreaPixels) return;
    const img = await new Promise((resolve, reject) => {
      const image = new Image();
      image.onload = () => resolve(image);
      image.onerror = reject;
      image.src = rawPhotoData;
    }).catch(() => null);
    if (!img) {
      app.toast('error', 'Falha no recorte', 'Não foi possível preparar a imagem.');
      return;
    }

    const outputSize = 320;
    const canvas = document.createElement('canvas');
    canvas.width = outputSize;
    canvas.height = outputSize;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      app.toast('error', 'Falha no recorte', 'Canvas indisponível no navegador.');
      return;
    }

    const { x, y, width, height } = croppedAreaPixels;

    ctx.clearRect(0, 0, outputSize, outputSize);
    ctx.drawImage(
      img,
      x,
      y,
      width,
      height,
      0,
      0,
      outputSize,
      outputSize,
    );

    const croppedData = canvas.toDataURL('image/jpeg', 0.92);
    setAvatarData(croppedData);
    setPhotoRemoved(false);
    setCropOpen(false);
    app.toast('success', 'Foto preparada', 'Clique em "Salvar alterações" para persistir.');
  };

  const confirmRemovePhoto = () => {
    setAvatarData('');
    setRawPhotoData('');
    setPhotoFileName('');
    setPhotoRemoved(true);
    setRemovePhotoOpen(false);
    app.toast('info', 'Foto removida', 'Clique em "Salvar alterações" para persistir.');
  };

  const openDeactivateModal = () => {
    if (isProtectedUser) {
      app.toast('info', 'Usuário protegido', 'O usuário ID 1 é protegido e não pode ser inativado.');
      return;
    }
    if (!canManageUsers || isCreating || !selectedUser?.id) return;
    setDeactivatePassword('');
    setConfirmDeactivateOpen(true);
  };

  const confirmDeactivateUser = async () => {
    if (!canManageUsers || isCreating || !selectedUser?.id) return;
    if (!String(deactivatePassword || '').trim()) {
      app.toast('error', 'Senha obrigatória', 'Informe sua senha para confirmar a exclusão.');
      return;
    }
    setDeactivating(true);
    try {
      await app.deactivateUser(selectedUser.id, deactivatePassword);
      setConfirmDeactivateOpen(false);
      app.toast('success', 'Usuário inativado', `${selectedUser.name || 'Usuário'} foi inativado com sucesso.`);
      app.navigate('users');
    } catch (error) {
      app.toast('error', 'Falha ao inativar usuário', error.message || 'Não foi possível inativar o usuário.');
    } finally {
      setDeactivating(false);
    }
  };

  return (
    <PShell active="users">
      <button type="button" className="sp-btn sp-btn-ghost sp-form-back" onClick={() => app.navigate('users')}>
        ← Voltar para usuários
      </button>
      <div className="sp-screen-intro">
        <div className="sp-caps sp-screen-head__caps">Gerenciamento de Usuários</div>
        <h1 className="sp-screen-head__title">{isCreating ? 'Criar novo usuário' : 'Detalhes da conta de usuário'}</h1>
        {readOnlyProfile && (
          <div className="sp-screen-head__meta">Visualização habilitada. Apenas Administrador pode alterar o cadastro de usuários.</div>
        )}
      </div>

      <div className="sp-profile-layout">
        <div className="sp-profile-stack">
          <div className="sp-card sp-profile-card">
            <div className="sp-profile-identity">
              <Avatar name={selectedUser?.name || name || 'Usuário'} size={60} src={photoRemoved ? '' : (avatarData || selectedUser?.avatar_data || '')} />
              <div className="sp-profile-identity-main">
                <div className="sp-profile-photo-top">
                  <div className="sp-profile-photo-controls">
                    <div className="sp-profile-photo-actions">
                      <button type="button" className="sp-btn sp-btn-outline" onClick={onPickPhoto} disabled={readOnlyProfile}>
                        {photoRemoved || !(avatarData || selectedUser?.avatar_data) ? 'Inserir foto' : 'Trocar foto'}
                      </button>
                      <button
                        type="button"
                        className="sp-btn sp-btn-ghost sp-btn-danger-text"
                        disabled={readOnlyProfile || photoRemoved || !(avatarData || selectedUser?.avatar_data)}
                        onClick={() => setRemovePhotoOpen(true)}
                      >
                        Excluir foto
                      </button>
                    </div>
                    <div className="sp-profile-hint">PNG ou JPG · até 2MB</div>
                  </div>
                  <div className="sp-profile-status-inline">
                    <label className="sp-caps sp-label">Status</label>
                    <select className={`sp-input${submitAttempted && fieldErrors.status ? ' sp-input--invalid' : ''}`} value={status} onChange={(e) => setStatus(e.target.value)} disabled={readOnlyProfile || statusLocked}>
                      <option value="active">Ativo</option>
                      <option value="inactive">Inativo</option>
                    </select>
                  </div>
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/png,image/jpeg"
                  className="sp-hidden-input"
                  onChange={onPhotoSelected}
                  disabled={readOnlyProfile}
                />
                {photoFileName && (
                  <div className="sp-mono sp-profile-file">{photoFileName}</div>
                )}
              </div>
            </div>
            <div className="sp-form-grid-2">
              <div>
                <label className="sp-caps sp-label">Nome completo</label>
                <input className={`sp-input${submitAttempted && fieldErrors.name ? ' sp-input--invalid' : ''}`} value={name} onChange={(e) => setName(e.target.value)} disabled={readOnlyProfile} />
              </div>
              <div>
                <label className="sp-caps sp-label">Cargo</label>
                <input
                  className={`sp-input${submitAttempted && fieldErrors.role ? ' sp-input--invalid' : ''}`}
                  value={role}
                  onChange={(e) => setRole(e.target.value)}
                  disabled={readOnlyProfile}
                />
              </div>
              <div>
                <label className="sp-caps sp-label">Departamento</label>
                <input className={`sp-input${submitAttempted && fieldErrors.department ? ' sp-input--invalid' : ''}`} value={department} onChange={(e) => setDepartment(e.target.value)} disabled={readOnlyProfile} />
              </div>
              <div>
                <div className="sp-contact-split">
                  <div>
                    <label className="sp-caps sp-label">Telefone</label>
                    <input className="sp-input" value={phone} onChange={(e) => setPhone(e.target.value)} disabled={readOnlyProfile} />
                  </div>
                  <div>
                    <label className="sp-caps sp-label">Whatsapp</label>
                    <input className="sp-input" value={whatsapp} onChange={(e) => setWhatsapp(e.target.value)} disabled={readOnlyProfile} />
                  </div>
                </div>
              </div>
              <div>
                <label className="sp-caps sp-label">Cidade/UF</label>
                <input className={`sp-input${submitAttempted && fieldErrors.cityUf ? ' sp-input--invalid' : ''}`} value={cityUf} onChange={(e) => setCityUf(e.target.value)} disabled={readOnlyProfile} />
              </div>
              <div>
                <label className="sp-caps sp-label">Fuso</label>
                <select className="sp-input" value={timezone} onChange={(e) => setTimezone(e.target.value)} disabled={readOnlyProfile}>
                  <option>América / São Paulo (GMT-3)</option>
                  <option>América / Araguaina (GMT-3)</option>
                  <option>América / Bahia (GMT-3)</option>
                  <option>América / Belem (GMT-3)</option>
                  <option>América / Fortaleza (GMT-3)</option>
                  <option>América / Maceio (GMT-3)</option>
                  <option>América / Recife (GMT-3)</option>
                  <option>América / Santarem (GMT-3)</option>
                  <option>América / Cuiaba (GMT-4)</option>
                  <option>América / Campo Grande (GMT-4)</option>
                  <option>América / Manaus (GMT-4)</option>
                  <option>América / Porto Velho (GMT-4)</option>
                  <option>América / Boa Vista (GMT-4)</option>
                  <option>América / Rio Branco (GMT-5)</option>
                  <option>América / Eirunepe (GMT-5)</option>
                  <option>América / Noronha (GMT-2)</option>
                </select>
              </div>
            </div>
          </div>

          <div className="sp-profile-sidecards">
            <div className="sp-card sp-profile-card">
              <div className="sp-profile-card__title sp-profile-card__title--tight">Credenciais de acesso</div>
              <div>
                <label className="sp-caps sp-label">E-mail</label>
                <input className={`sp-input${submitAttempted && fieldErrors.email ? ' sp-input--invalid' : ''}`} value={email} onChange={(e) => setEmail(e.target.value)} disabled={readOnlyProfile} />
              </div>
              <div className="sp-form-grid-2 sp-credentials-grid">
                <div>
                  <label className="sp-caps sp-label">Senha</label>
                  <input
                    type="password"
                    className={`sp-input${submitAttempted && fieldErrors.password ? ' sp-input--invalid' : ''}`}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    autoComplete="new-password"
                    disabled={readOnlyProfile}
                  />
                </div>
                <div>
                  <label className="sp-caps sp-label">Confirmar senha</label>
                  <input
                    type="password"
                    className={`sp-input${submitAttempted && fieldErrors.passwordConfirm ? ' sp-input--invalid' : ''}`}
                    value={passwordConfirm}
                    onChange={(e) => setPasswordConfirm(e.target.value)}
                    autoComplete="new-password"
                    disabled={readOnlyProfile}
                  />
                </div>
              </div>
            </div>

            <div className="sp-card sp-profile-card">
              <div className="sp-profile-card__title sp-profile-card__title--tight">Minhas permissões</div>
              <div className="sp-profile-perm-lead">Permissões são concedidas por um administrador.</div>
              <div className="sp-profile-perm-grid">
                {PERMISSION_LEVELS.map((p) => {
                  const active = perms.includes(p);
                  return (
                  <button
                    key={p}
                    type="button"
                    className={`sp-profile-perm-tile${active ? ' is-active' : ''}`}
                    onClick={() => togglePermission(p)}
                    disabled={readOnlyProfile || permissionsLocked}
                  >
                    <div className="sp-profile-perm-tile__head">
                      <div className="sp-profile-perm-tile__icon"><Icon name="check" size={11} stroke={3} /></div>
                      <div className="sp-mono sp-profile-perm-tile__code">{p}</div>
                    </div>
                    <div className="sp-profile-perm-tile__desc">
                      {p === 'Registrador' && 'Registra e prepara pagamentos no sistema'}
                      {p === 'Aprovador' && 'Analisa e aprova pagamentos para integração'}
                      {p === 'Administrador' && 'Gerencia usuários, empresas e configurações'}
                    </div>
                  </button>
                );
                })}
              </div>
            </div>
          </div>

          <div className="sp-profile-save-row">
            <button type="button" className="sp-btn sp-btn-outline" onClick={() => app.navigate('users')}>Voltar</button>
            {canManageUsers && (
              <>
                {!isCreating && !isProtectedUser && (
                  <button type="button" className="sp-btn sp-btn-ghost sp-form-footer__danger" onClick={openDeactivateModal}>
                    Excluir
                  </button>
                )}
                <button type="button" className="sp-btn sp-btn-accent" onClick={saveProfile} disabled={saving}>{saving ? 'Salvando...' : 'Salvar alterações'}</button>
              </>
            )}
          </div>
        </div>
      </div>

      <Modal open={removePhotoOpen} onClose={() => setRemovePhotoOpen(false)} width={440}>
        <div className="sp-modal__head">
          <div className="sp-modal__head-title">Excluir foto de perfil</div>
          <button type="button" onClick={() => setRemovePhotoOpen(false)} className="sp-btn sp-btn-ghost sp-modal__close">
            <Icon name="x" size={14} />
          </button>
        </div>
        <div className="sp-modal__body sp-modal__body--p20">
          <div className="sp-sidebar-card__muted">
            Deseja realmente excluir a foto deste usuário?
          </div>
          <div className="sp-modal-actions sp-modal-actions--mt">
            <button type="button" className="sp-btn sp-btn-outline" onClick={() => setRemovePhotoOpen(false)}>Cancelar</button>
            <button type="button" className="sp-btn sp-btn-danger-solid" onClick={confirmRemovePhoto}>Excluir foto</button>
          </div>
        </div>
      </Modal>

      <Modal open={confirmDeactivateOpen} onClose={() => !deactivating && setConfirmDeactivateOpen(false)} width={520}>
        <div className="sp-modal__head">
          <div>
            <div className="sp-modal__head-title">Excluir usuário</div>
            <div className="sp-modal__head-desc">A conta será inativada, o e-mail será embaralhado e não aparecerá mais na lista de usuários.</div>
          </div>
          <button type="button" onClick={() => !deactivating && setConfirmDeactivateOpen(false)} className="sp-btn sp-btn-ghost sp-modal__close" disabled={deactivating}>
            <Icon name="x" size={14} />
          </button>
        </div>
        <div className="sp-modal__body sp-modal__body--stack-lg">
          <div className="sp-sidebar-card__muted">
            O usuário <strong className="sp-text-strong">{selectedUser?.name || 'selecionado'}</strong> será inativado.
          </div>
          <div className="sp-delete-banner">
            Confirme sua senha de Administrador para continuar.
          </div>
          <div>
            <label className="sp-caps sp-label">Senha do usuário Administrador</label>
            <input
              className="sp-input"
              type="password"
              value={deactivatePassword}
              onChange={(e) => setDeactivatePassword(e.target.value)}
              placeholder="Digite sua senha"
              disabled={deactivating}
            />
          </div>
          <div className="sp-modal-actions">
            <button type="button" className="sp-btn sp-btn-outline" onClick={() => setConfirmDeactivateOpen(false)} disabled={deactivating}>Cancelar</button>
            <button type="button" className="sp-btn sp-btn-danger-solid" onClick={confirmDeactivateUser} disabled={deactivating}>
              {deactivating ? 'Inativando...' : 'Sim, inativar usuário'}
            </button>
          </div>
        </div>
      </Modal>

      <Modal open={cropOpen} onClose={() => setCropOpen(false)} width={560}>
        <div className="sp-modal__head">
          <div>
            <div className="sp-modal__head-title">Ajustar foto de perfil</div>
            <div className="sp-modal__head-desc">Defina a posição da imagem antes de salvar.</div>
          </div>
          <button type="button" onClick={() => setCropOpen(false)} className="sp-btn sp-btn-ghost sp-modal__close">
            <Icon name="x" size={14} />
          </button>
        </div>
        <div className="sp-modal__body sp-modal__body--crop">
          <div className="sp-crop-stage">
            {rawPhotoData && (
              <Cropper
                image={rawPhotoData}
                crop={crop}
                zoom={cropZoom}
                aspect={1}
                cropShape="round"
                showGrid={false}
                onCropChange={setCrop}
                onZoomChange={setCropZoom}
                onCropComplete={(_, pixels) => setCroppedAreaPixels(pixels)}
              />
            )}
          </div>
          <div>
            <label className="sp-caps sp-label">Zoom</label>
            <input type="range" min="1" max="2.2" step="0.01" value={cropZoom} onChange={(e) => setCropZoom(Number(e.target.value))} className="sp-range-full" />
          </div>
          <div className="sp-modal-actions">
            <button type="button" className="sp-btn sp-btn-outline" onClick={() => setCropOpen(false)}>Cancelar</button>
            <button type="button" className="sp-btn sp-btn-accent" onClick={applyCrop}>Aplicar recorte</button>
          </div>
        </div>
      </Modal>
    </PShell>
  );
}
